import { faChevronDown, faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener } from '@material-ui/core';
import { WaitlistFormServiceOption } from 'actions/waitlists/WaitlistFormApi';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { FunctionComponent, useState } from 'react';
import styles from './WaitlistServiceSelect.module.scss';

interface WaitlistServiceSelectProps {
    services: Array<WaitlistFormServiceOption>;
    value?: number;
    onChange: (serviceId: number) => void;
}

// "1 h 30 min", "45 min"
const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;

    return [hours > 0 ? `${hours} h` : '', rest > 0 || hours === 0 ? `${rest} min` : ''].filter(Boolean).join(' ');
};

// Services select of the "Pets & Services" step: one row per service with its duration and price.
export const WaitlistServiceSelect: FunctionComponent<WaitlistServiceSelectProps> = ({ services, value, onChange }) => {
    const [open, setOpen] = useState(false);

    const selected = services.find(service => service.id === value);

    return (
        <div className={styles.root}>
            <Label>Services</Label>

            <ClickAwayListener onClickAway={() => setOpen(false)}>
                <div className={clsx(styles.box, { [styles.boxOpen]: open })}>
                    <button
                        type="button"
                        className={styles.field}
                        aria-expanded={open}
                        aria-haspopup="listbox"
                        onClick={() => setOpen(current => !current)}
                    >
                        <span className={clsx(styles.value, { [styles.placeholder]: !selected })}>
                            {selected?.name ?? 'Select a Service'}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={clsx(styles.chevron, { [styles.chevronOpen]: open })}
                        />
                    </button>

                    {open && (
                        <div className={styles.dropdown}>
                            <p className={styles.section}>Services</p>

                            <ul
                                role="listbox"
                                aria-label="Services"
                                className={styles.list}
                            >
                                {services.map(service => (
                                    <li
                                        key={service.id}
                                        role="option"
                                        aria-selected={service.id === value}
                                    >
                                        <button
                                            type="button"
                                            className={clsx(styles.row, { [styles.rowSelected]: service.id === value })}
                                            onClick={() => {
                                                onChange(service.id);
                                                setOpen(false);
                                            }}
                                        >
                                            <span className={styles.name}>{service.name}</span>
                                            <span className={styles.duration}>
                                                {service.durationInMinutes !== null &&
                                                    service.durationInMinutes > 0 && (
                                                        <>
                                                            <FontAwesomeIcon icon={faClock} />
                                                            {formatDuration(service.durationInMinutes)}
                                                        </>
                                                    )}
                                            </span>
                                            <span className={styles.price}>
                                                {service.price !== null && `$${service.price.toFixed(2)}`}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default WaitlistServiceSelect;
